/**
 * Yushe header behaviour (sections/header-v4.liquid). Ported from the Next.js Header, AnnouncementBar,
 * MegaMenu, MobileMenu and useScrollDirection.
 * - The announcement bar fades through its messages every 5 s, pauses on hover/focus, and a dismissal
 *   lasts for the session (sessionStorage).
 * - The bar tucks away while scrolling down past 160px and returns on scroll up, except while the homepage
 *   billboard film fills the screen (its first scrolls): there a scroll up leaves it tucked, so the film stays whole.
 * - Desktop: hovering a trigger (after a short intent delay) drops the curtain; the pointer may travel
 *   into the panel; clicking toggles; Esc closes and hands focus back. Hovering or focusing a word
 *   fills it with its photo and swaps the preview.
 * - Phone/tablet (port of BottomNav and MobileMenu): the dock's Menu opens the menu sheet, a <dialog> opened
 *   non-modally so the dock can stay above it (reading "Close"); focus trap, Esc and scroll lock are here, as in
 *   Sheet.tsx. Search opens Horizon's search modal, the bag opens the cart drawer; from inside the menu both
 *   replace it. The dock keeps its own bag count and shows "Added to bag" when the count rises after load.
 * The element re-initialises on its own when the theme editor re-renders the section.
 */
const OPEN_DELAY = 90;
const CLOSE_DELAY = 220;
const CLOSE_MS = 450;
const NOTE_MS = 2200;
const DESKTOP = '(min-width: 1100px)';
const ANNOUNCE_KEY = 'yushe:announce-dismissed';
const ANNOUNCE_INTERVAL = 5000;
const FOCUSABLE = 'a[href], button:not([disabled]), input:not([disabled]), select, textarea, [tabindex]:not([tabindex="-1"])';

/** A light tick under the thumb where the device supports it (Android); silently nothing elsewhere. */
const tick = () => navigator.vibrate?.(6);

class YusheHeader extends HTMLElement {
  connectedCallback() {
    this.mega = this.querySelector('.mega');
    this.triggers = [...this.querySelectorAll('.hdr__trigger')];
    this.sections = [...this.querySelectorAll('.mega__section')];
    const section = this.closest('.shopify-section') || this.parentElement;
    this.scrim = section.querySelector('.mega-scrim');
    this.menu = section.querySelector('.mmenu');
    this.panel = this.menu && this.menu.querySelector('.sheet__panel');
    this.dock = section.querySelector('.dock');
    this.dockMenu = this.dock && this.dock.querySelector('.dock__menu');
    this.searchModal = document.getElementById('search-modal');
    this.announce = section.querySelector('.announce');
    this.openId = null;
    this.menuOpen = false;
    this.timer = 0;
    this.frame = 0;
    this.lastY = window.scrollY;
    this.ac = new AbortController();
    const { signal } = this.ac;
    const designMode = window.Shopify && window.Shopify.designMode;

    // ---- Announcement bar ----
    if (this.announce) {
      let dismissed = false;
      try {
        dismissed = !designMode && !!sessionStorage.getItem(ANNOUNCE_KEY);
      } catch (e) {
        dismissed = false;
      }
      if (dismissed) this.announce.hidden = true;
      const items = [...this.announce.querySelectorAll('.announce__item')];
      let index = 0;
      let paused = false;
      const show = (n) => {
        index = n;
        items.forEach((li, i) => {
          li.dataset.active = String(i === index);
          li.setAttribute('aria-hidden', String(i !== index));
          const a = li.querySelector('a');
          if (a) a.tabIndex = i === index ? 0 : -1;
        });
      };
      if (items.length > 1 && !dismissed) {
        this.announceTimer = setInterval(() => {
          if (!paused) show((index + 1) % items.length);
        }, ANNOUNCE_INTERVAL);
      }
      this.announce.addEventListener('pointerenter', () => (paused = true), { signal });
      this.announce.addEventListener('pointerleave', () => (paused = false), { signal });
      this.announce.addEventListener('focusin', () => (paused = true), { signal });
      this.announce.addEventListener('focusout', () => (paused = false), { signal });
      const close = this.announce.querySelector('[data-hdr="dismiss-announce"]');
      if (close) {
        close.addEventListener('click', () => {
          try {
            sessionStorage.setItem(ANNOUNCE_KEY, '1');
          } catch (e) {
            /* private mode: dismiss for this page only */
          }
          this.announce.hidden = true;
          clearInterval(this.announceTimer);
        }, { signal });
      }
    }

    // ---- Hide on scroll ----
    const update = () => {
      this.frame = 0;
      // Horizon's dialogs (search, filters) lock scroll by fixing the body and restore the position on close;
      // neither jump is the user scrolling, so ignore them and keep the last reading.
      if (document.body.style.position === 'fixed') return;
      const y = Math.max(0, window.scrollY);
      const delta = y - this.lastY;
      if (Math.abs(delta) < 8 && y > 0) return;
      const hidden = y > 160 && (delta > 0 || (this.hiddenByScroll && this.overFilm()));
      this.lastY = y;
      this.dataset.scrolled = String(y > 8);
      this.hiddenByScroll = hidden;
      this.applyHidden();
    };
    if (!designMode) {
      window.addEventListener('scroll', () => {
        if (!this.frame) this.frame = requestAnimationFrame(update);
      }, { passive: true, signal });
    }
    this.hiddenByScroll = false;
    this.searchOpen = false;
    this.dataset.scrolled = String(window.scrollY > 8);
    this.applyHidden();
    // The bar never stays tucked while search is open (Horizon's dialog events fire on the component itself).
    if (this.searchModal) {
      this.searchModal.addEventListener('dialog:open', () => {
        this.searchOpen = true;
        this.applyHidden();
        // Opening search means "I want to type": the field takes focus (showModal would leave it on the panel).
        const field = this.searchModal.querySelector('input[type="search"]');
        if (field) requestAnimationFrame(() => field.focus({ preventScroll: true }));
      }, { signal });
      this.searchModal.addEventListener('dialog:close', () => {
        this.searchOpen = false;
        this.applyHidden();
      }, { signal });
    }

    // ---- Mega menu ----
    for (const t of this.triggers) {
      t.addEventListener('pointerenter', (e) => {
        if (e.pointerType !== 'mouse') return;
        clearTimeout(this.timer);
        if (this.openId) return this.openMega(t.dataset.group);
        this.timer = setTimeout(() => this.openMega(t.dataset.group), OPEN_DELAY);
      }, { signal });
      t.addEventListener('click', () => {
        clearTimeout(this.timer);
        if (this.openId === t.dataset.group) this.closeMega();
        else this.openMega(t.dataset.group);
      }, { signal });
    }
    this.addEventListener('pointerleave', (e) => {
      if (e.pointerType !== 'mouse' || !this.openId) return;
      clearTimeout(this.timer);
      this.timer = setTimeout(() => this.closeMega(), CLOSE_DELAY);
    }, { signal });
    this.addEventListener('pointerenter', () => {
      if (this.openId) clearTimeout(this.timer);
    }, { signal });
    this.addEventListener('focusout', (e) => {
      if (this.openId && !this.contains(e.relatedTarget)) this.closeMega();
    }, { signal });
    document.addEventListener('keydown', (e) => {
      if (e.key !== 'Escape' || !this.openId) return;
      const t = this.querySelector(`#mega-trigger-${this.openId}`);
      this.closeMega();
      if (t) t.focus();
    }, { signal });
    if (this.scrim) this.scrim.addEventListener('click', () => this.closeMega(), { signal });

    for (const s of this.sections) {
      // Leaving a word clears it straight away (after a beat, so sliding to the next word does not flicker).
      let coolTimer = 0;
      for (const w of s.querySelectorAll('.mword')) {
        const hot = () => {
          clearTimeout(coolTimer);
          this.setHot(s, Number(w.dataset.idx));
        };
        w.addEventListener('pointerenter', hot, { signal });
        w.addEventListener('focus', hot, { signal });
        w.addEventListener('pointerleave', () => {
          clearTimeout(coolTimer);
          coolTimer = setTimeout(() => this.setHot(s, null), 90);
        }, { signal });
        w.addEventListener('click', () => this.closeMega(), { signal });
      }
      this.setHot(s, null);
    }

    // ---- Phone / tablet menu sheet ----
    if (this.menu) {
      for (const el of this.menu.querySelectorAll('[data-hdr="close-menu"]')) {
        el.addEventListener('click', () => this.closeMenu(), { signal });
      }
      for (const a of this.menu.querySelectorAll('a[href]')) {
        a.addEventListener('click', (e) => {
          const url = new URL(a.href, location.href);
          const samePage = url.pathname === location.pathname && url.search === location.search;
          const newTab = (a.target && a.target !== '_self') || a.hasAttribute('download');
          if (e.defaultPrevented || e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey || newTab || url.origin !== location.origin || samePage) {
            this.closeMenu();
            return;
          }
          // Going to another page: the sheet holds (instead of dropping to the old page while the next one
          // loads); the tapped link stays, the rest step back, a hairline runs under it. html[data-menu-nav]
          // keeps the old page's main out of the swap, so the new page rises straight up through the menu.
          a.dataset.going = '';
          this.menu.dataset.leaving = '';
          document.documentElement.dataset.menuNav = '';
          // If the page never unloads (navigation cancelled, a file response, a very slow server), do not leave
          // the menu frozen in its leaving state: put it away after a few seconds.
          clearTimeout(this.leaveTimer);
          this.leaveTimer = setTimeout(() => {
            if (this.menu.hasAttribute('data-leaving')) this.closeMenu();
          }, 6000);
        }, { signal });
      }
      // Back/forward cache restores the page as it was left: put the menu away.
      window.addEventListener('pageshow', (e) => {
        if (!e.persisted) return;
        delete document.documentElement.dataset.menuNav;
        if (this.menu.hasAttribute('data-leaving')) {
          delete this.menu.dataset.leaving;
          for (const el of this.menu.querySelectorAll('[data-going]')) delete el.dataset.going;
          this.closeMenu();
        }
      }, { signal });
      // Esc closes; Tab stays inside the sheet (Sheet.tsx).
      document.addEventListener('keydown', (e) => {
        if (!this.menuOpen) return;
        if (e.key === 'Escape') {
          e.preventDefault();
          this.closeMenu();
          return;
        }
        if (e.key !== 'Tab' || !this.panel) return;
        const items = [...this.panel.querySelectorAll(FOCUSABLE)].filter((el) => el.offsetParent);
        if (!items.length) return;
        const first = items[0];
        const last = items[items.length - 1];
        if (e.shiftKey && document.activeElement === first) {
          e.preventDefault();
          last.focus();
        } else if (!e.shiftKey && document.activeElement === last) {
          e.preventDefault();
          first.focus();
        }
      }, { signal });
      const mq = window.matchMedia(DESKTOP);
      mq.addEventListener('change', () => mq.matches && this.menuOpen && this.closeMenu(), { signal });
    }

    // ---- Dock (phone / tablet) ----
    if (this.dock) {
      this.dockBag = this.dock.querySelector('.dock__bag');
      this.dockCount = this.dock.querySelector('.dock__count');
      this.dockNote = this.dock.querySelector('.dock__note');
      this.count = Number(this.dockBag && this.dockBag.dataset.count) || 0;
      this.reseed = false;

      if (this.dockMenu) {
        this.dockMenu.addEventListener('click', () => {
          tick();
          if (this.menuOpen) this.closeMenu();
          else this.openMenu();
        }, { signal });
      }
      // From inside the menu, search and the bag replace it rather than stacking on top of it.
      const search = this.dock.querySelector('.dock__search');
      if (search) {
        search.addEventListener('click', () => {
          tick();
          this.closeMenu();
          this.openSearch();
        }, { signal });
      }
      if (this.dockBag) {
        this.dockBag.addEventListener('click', (e) => {
          // Plain taps open the drawer; modified clicks (new tab/window) still go to /cart. On /cart itself the
          // bag is already on screen, so the tap does nothing new.
          if (e.metaKey || e.ctrlKey || e.shiftKey || e.button !== 0) return;
          e.preventDefault();
          tick();
          if (this.dockBag.getAttribute('aria-current') === 'page') return;
          this.closeMenu();
          this.openBag();
        }, { signal });
      }

      // Bag count: every add or change fires `cart:update` with detail.data.itemCount (Horizon's event shape).
      document.addEventListener('cart:update', (e) => {
        const n = e.detail && e.detail.data && e.detail.data.itemCount;
        if (typeof n === 'number') this.setCount(n);
      }, { signal });
      // A page restored from the back/forward cache re-reads the cart (yushe-cart.js): that's not an add.
      window.addEventListener('pageshow', (e) => {
        if (e.persisted) this.reseed = true;
      }, { signal });
    }
  }

  disconnectedCallback() {
    this.ac.abort();
    clearTimeout(this.timer);
    clearTimeout(this.focusTimer);
    clearTimeout(this.menuTimer);
    clearTimeout(this.noteTimer);
    clearInterval(this.announceTimer);
    cancelAnimationFrame(this.frame);
    this.unlockScroll();
  }

  /** The homepage billboard is uncovered (the Showroom's room has cleared) and fills the screen. */
  overFilm() {
    const bb = document.querySelector('.bb');
    if (!bb) return false;
    const room = document.querySelector('.shw:not([data-editor])');
    if (room && !room.hasAttribute('data-flown')) return false;
    const r = bb.getBoundingClientRect();
    return r.top <= window.innerHeight * 0.1 && r.bottom >= window.innerHeight * 0.5;
  }

  applyHidden() {
    const tucked = this.hiddenByScroll && !this.openId && !this.menuOpen && !this.searchOpen;
    this.dataset.hidden = String(Boolean(tucked));
  }

  openSearch() {
    if (this.searchModal && typeof this.searchModal.showDialog === 'function') this.searchModal.showDialog();
    else window.location.href = (window.Theme && window.Theme.routes && window.Theme.routes.search_url) || '/search';
  }

  openBag() {
    const drawer = document.querySelector('yushe-cart-drawer');
    if (drawer && typeof drawer.open === 'function') drawer.open();
    else if (this.dockBag) window.location.href = this.dockBag.href;
  }

  /** Updates the dock's count; a rise after page load shows the "Added to bag" note. */
  setCount(n) {
    const prev = this.count;
    this.count = n;
    if (this.dockBag) this.dockBag.setAttribute('aria-label', `Bag, ${n} ${n === 1 ? 'item' : 'items'}`);
    if (this.dockCount && n !== prev) {
      this.dockCount.textContent = n > 99 ? '99+' : String(n);
      this.dockCount.hidden = n <= 0;
      // Replay the bump.
      this.dockCount.style.animation = 'none';
      void this.dockCount.offsetWidth;
      this.dockCount.style.animation = '';
    }
    if (this.reseed) {
      this.reseed = false;
      return;
    }
    if (n > prev) this.showNote();
  }

  showNote() {
    if (!this.dockNote) return;
    clearTimeout(this.noteTimer);
    const btn = document.createElement('button');
    btn.type = 'button';
    btn.className = 'dock__note-btn';
    btn.append('Added to bag ');
    const view = document.createElement('span');
    view.textContent = 'View';
    btn.append(view);
    btn.addEventListener('click', () => {
      tick();
      this.closeMenu();
      this.openBag();
    });
    this.dockNote.replaceChildren(btn);
    this.noteTimer = setTimeout(() => this.dockNote.replaceChildren(), NOTE_MS);
  }

  setMenuState(open) {
    if (this.dock) this.dock.toggleAttribute('data-menu', open);
    if (this.dockMenu) {
      this.dockMenu.setAttribute('aria-expanded', String(open));
      this.dockMenu.setAttribute('aria-label', open ? 'Close menu' : 'Open menu');
    }
  }

  openMega(id) {
    if (!window.matchMedia(DESKTOP).matches) return;
    this.openId = id;
    this.dataset.mega = 'true';
    this.mega.dataset.open = 'true';
    if (this.scrim) this.scrim.dataset.open = 'true';
    for (const t of this.triggers) t.setAttribute('aria-expanded', String(t.dataset.group === id));
    for (const s of this.sections) {
      const active = s.dataset.group === id;
      s.dataset.active = String(active);
      if (active) s.removeAttribute('inert');
      else s.setAttribute('inert', '');
      this.setHot(s, null);
    }
    this.applyHidden();
  }

  closeMega() {
    clearTimeout(this.timer);
    if (!this.openId) return;
    this.openId = null;
    this.dataset.mega = 'false';
    this.mega.dataset.open = 'false';
    if (this.scrim) this.scrim.dataset.open = 'false';
    for (const t of this.triggers) t.setAttribute('aria-expanded', 'false');
    for (const s of this.sections) {
      s.dataset.active = 'false';
      s.setAttribute('inert', '');
    }
    this.applyHidden();
  }

  /** Marks word `idx` hot in section `s` (null: nothing hot, the first preview shows). */
  setHot(s, idx) {
    const shown = idx === null ? 0 : idx;
    for (const w of s.querySelectorAll('.mword')) {
      if (idx !== null && Number(w.dataset.idx) === idx) w.dataset.hot = '';
      else delete w.dataset.hot;
    }
    if (idx === null) delete s.dataset.hot;
    else s.dataset.hot = '';
    for (const el of s.querySelectorAll('.mprev [data-idx]')) {
      if (Number(el.dataset.idx) === shown) el.dataset.on = '';
      else delete el.dataset.on;
    }
  }

  /* Non-modal (show(), not showModal()): a modal dialog sits in the top layer, and the dock has to stay above
     the sheet as its close control. Focus trap, Esc and scroll lock are handled here instead (Sheet.tsx). */
  openMenu() {
    if (this.menuOpen || !this.menu) return;
    this.menuOpen = true;
    this.closeMega();
    clearTimeout(this.menuTimer);
    this.opener = document.activeElement;
    this.lockScroll();
    if (!this.menu.open) this.menu.show();
    this.setMenuState(true);
    requestAnimationFrame(() => requestAnimationFrame(() => {
      if (this.menuOpen) this.menu.dataset.state = 'open';
    }));
    clearTimeout(this.focusTimer);
    this.focusTimer = setTimeout(() => {
      const first = this.panel && this.panel.querySelector(FOCUSABLE);
      if (first) first.focus();
    }, 50);
    this.applyHidden();
  }

  /* Scroll and focus are handed back at once (as Sheet.tsx does), so search or the bag can open straight
     after; the dialog itself closes when the fade has played. */
  closeMenu() {
    if (!this.menuOpen) return;
    this.menuOpen = false;
    clearTimeout(this.focusTimer);
    clearTimeout(this.leaveTimer);
    delete this.menu.dataset.leaving;
    for (const el of this.menu.querySelectorAll('[data-going]')) delete el.dataset.going;
    delete document.documentElement.dataset.menuNav;
    this.menu.dataset.state = 'closed';
    this.setMenuState(false);
    this.unlockScroll();
    if (this.opener && this.opener.focus && this.opener !== document.body) this.opener.focus({ preventScroll: true });
    this.opener = null;
    clearTimeout(this.menuTimer);
    this.menuTimer = setTimeout(() => {
      if (!this.menuOpen && this.menu.open) this.menu.close();
    }, CLOSE_MS);
    this.applyHidden();
  }

  lockScroll() {
    const root = document.documentElement;
    const gap = window.innerWidth - root.clientWidth;
    this.prevScroll = { overflow: root.style.overflow, pad: root.style.paddingRight };
    root.style.overflow = 'hidden';
    if (gap) root.style.paddingRight = `${gap}px`;
  }

  unlockScroll() {
    if (!this.prevScroll) return;
    const root = document.documentElement;
    root.style.overflow = this.prevScroll.overflow;
    root.style.paddingRight = this.prevScroll.pad;
    this.prevScroll = null;
  }
}

if (!customElements.get('yushe-header')) customElements.define('yushe-header', YusheHeader);
